// checkout.ts — the Stripe hand-off used when a buyer resumes a plan intent
// after signing up (see lib/pendingIntent.ts and ResumeCheckoutPrompt).
//
// Same server contract Pricing.tsx's handleCheckout uses: POST
// /billing/checkout returns a hosted-checkout URL, and the server answers 409
// { code: "already_subscribed" } for anyone it would double-bill, in which case
// plan changes belong in the Stripe Customer Portal instead. Kept separate
// from Pricing.tsx (not imported from it) so the in-app resume step does not
// depend on that page's component internals.

import { apiClient } from "./apiClient";
import type { BillingInterval, PaidPlanIntent } from "./pendingIntent";

export interface CheckoutTarget {
  url: string;
  /** "portal" when the account already has a subscription and the plan has
   *  to change through the Customer Portal rather than a second checkout. */
  via: "checkout" | "portal";
}

export async function requestCheckoutUrl(plan: PaidPlanIntent, interval: BillingInterval): Promise<CheckoutTarget> {
  try {
    const { data } = await apiClient.post<{ url: string }>("/billing/checkout", { plan, interval });
    return { url: data.url, via: "checkout" };
  } catch (err: any) {
    if (err?.response?.status === 409 && err?.response?.data?.code === "already_subscribed") {
      const { data } = await apiClient.post<{ url: string }>("/billing/portal");
      return { url: data.url, via: "portal" };
    }
    throw err;
  }
}
